/**
 * Parsing e rolagem de expressões de dado ("2d6+3", "d20", "4d6-1").
 *
 * O RNG é injetado de propósito: o servidor passa um gerador criptográfico e os
 * testes passam um gerador determinístico.
 */

export const DICE_SIDES = [4, 6, 8, 10, 12, 20, 100] as const;
export type DieSides = (typeof DICE_SIDES)[number];

/** Retorna um inteiro em [0, maxExclusive). */
export type Rng = (maxExclusive: number) => number;

export class DiceExpressionError extends Error {
  override readonly name = 'DiceExpressionError';
}

export type ParsedDiceExpression = {
  /** forma normalizada, ex. "2d6+3" */
  source: string;
  count: number;
  sides: DieSides;
  modifier: number;
};

export type RolledDie = { sides: DieSides; value: number };

export type RollOutcome = {
  expression: ParsedDiceExpression;
  dice: RolledDie[];
  modifier: number;
  total: number;
};

export const DICE_LIMITS = {
  maxCount: 100,
  maxModifier: 999,
} as const;

const EXPRESSION_RE = /^(\d*)d(\d+)([+-]\d+)?$/;

const SUPPORTED = new Set<number>(DICE_SIDES);

function isSupportedSides(value: number): value is DieSides {
  return SUPPORTED.has(value);
}

/**
 * Converte texto em uma expressão de dado validada.
 * @throws {DiceExpressionError} quando o formato ou os limites são inválidos.
 */
export function parseDiceExpression(input: string): ParsedDiceExpression {
  if (typeof input !== 'string') {
    throw new DiceExpressionError('expressão deve ser texto');
  }
  const text = input.trim().toLowerCase().replace(/\s+/g, '');
  if (text.length === 0) {
    throw new DiceExpressionError('expressão vazia');
  }

  const match = EXPRESSION_RE.exec(text);
  if (!match) {
    throw new DiceExpressionError(
      `expressão inválida: "${input}" (use o formato NdM, por exemplo 2d6+3)`,
    );
  }

  const countText = match[1] ?? '';
  const sidesText = match[2] ?? '';
  const modifierText = match[3] ?? '';

  const count = countText === '' ? 1 : Number(countText);
  const sides = Number(sidesText);
  const modifier = modifierText === '' ? 0 : Number(modifierText);

  if (!Number.isInteger(count) || count < 1 || count > DICE_LIMITS.maxCount) {
    throw new DiceExpressionError(`quantidade de dados deve estar entre 1 e ${DICE_LIMITS.maxCount}`);
  }
  if (!Number.isInteger(sides) || !isSupportedSides(sides)) {
    throw new DiceExpressionError(
      `dado d${sidesText} não suportado (disponíveis: ${DICE_SIDES.map((s) => `d${s}`).join(', ')})`,
    );
  }
  if (!Number.isInteger(modifier) || Math.abs(modifier) > DICE_LIMITS.maxModifier) {
    throw new DiceExpressionError(`modificador deve estar entre -${DICE_LIMITS.maxModifier} e ${DICE_LIMITS.maxModifier}`);
  }

  const normalized = `${count}d${sides}${modifier === 0 ? '' : modifier > 0 ? `+${modifier}` : `${modifier}`}`;

  return { source: normalized, count, sides, modifier };
}

/** Rola uma expressão já validada. */
export function rollParsed(expression: ParsedDiceExpression, rng: Rng): RollOutcome {
  const dice: RolledDie[] = [];
  let sum = 0;
  for (let i = 0; i < expression.count; i += 1) {
    const raw = rng(expression.sides);
    const value = Math.min(expression.sides, Math.max(1, Math.floor(raw) + 1));
    dice.push({ sides: expression.sides, value });
    sum += value;
  }
  return {
    expression,
    dice,
    modifier: expression.modifier,
    total: sum + expression.modifier,
  };
}

/** Atalho: valida e rola de uma vez. */
export function roll(input: string, rng: Rng): RollOutcome {
  return rollParsed(parseDiceExpression(input), rng);
}

/** Detalhamento legível para o chat: "2d6+3 = [4, 2] + 3 = 9". */
export function describeRoll(outcome: RollOutcome): string {
  const values = outcome.dice.map((d) => d.value);
  const parts = [`[${values.join(', ')}]`];
  if (outcome.modifier !== 0) {
    parts.push(outcome.modifier > 0 ? `+ ${outcome.modifier}` : `- ${Math.abs(outcome.modifier)}`);
  }
  return `${outcome.expression.source} = ${parts.join(' ')} = ${outcome.total}`;
}

/**
 * RNG determinístico (mulberry32) para testes e replays.
 * NÃO use no servidor: lá o correto é crypto.randomInt.
 */
export function createSeededRng(seed: number): Rng {
  let state = seed >>> 0;
  return (maxExclusive: number): number => {
    if (!Number.isInteger(maxExclusive) || maxExclusive <= 0) {
      throw new DiceExpressionError('maxExclusive deve ser inteiro positivo');
    }
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    const result = ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    return Math.floor(result * maxExclusive);
  };
}
